import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { getUser, getRole } from "@/lib/auth";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, KeyRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_COLORS = {
  super_admin: "destructive",
  project_admin: "secondary",
  manager: "default",
  viewer: "outline",
};

const ROLE_LABELS = {
  super_admin: "Super Admin",
  project_admin: "Project Admin",
  manager: "Manager",
  viewer: "Viewer",
};

// ─── PROJECT DIALOGS ────────────────────────────────────────────────────────

function ProjectDialog({ open, onClose, onSaved, initial }) {
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setName(initial?.name ?? "");
    setDescription(initial?.description ?? "");
  }, [initial, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (initial) {
        await axios.put(`${API}/projects/${initial.id}`, { name, description });
        toast.success("Project updated");
      } else {
        await axios.post(`${API}/projects`, { name, description });
        toast.success("Project created");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error saving project");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
        <DialogHeader>
          <DialogTitle className="text-sm uppercase tracking-wider text-zinc-300">
            {initial ? "Edit Project" : "New Project"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <Label className="text-xs text-zinc-400 uppercase tracking-wider">Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="bg-zinc-800 border-zinc-700 text-white rounded-none"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-zinc-400 uppercase tracking-wider">Description</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="bg-zinc-800 border-zinc-700 text-white rounded-none"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none border-zinc-700 text-zinc-300">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="rounded-none bg-orange-600 hover:bg-orange-700">
              {loading ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── USER DIALOGS ─────────────────────────────────────────────────────────────

function UserDialog({ open, onClose, onSaved, initial, projects }) {
  const callerRole = getRole();
  const availableRoles =
    callerRole === "super_admin"
      ? ["super_admin", "project_admin", "manager", "viewer"]
      : ["manager", "viewer"];

  const [username, setUsername] = useState(initial?.username ?? "");
  const [email, setEmail] = useState(initial?.email ?? "");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState(initial?.role ?? "viewer");
  const [selectedProjectIds, setSelectedProjectIds] = useState(initial?.project_ids ?? []);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setUsername(initial?.username ?? "");
    setEmail(initial?.email ?? "");
    setPassword("");
    setRole(initial?.role ?? "viewer");
    setSelectedProjectIds(initial?.project_ids ?? []);
  }, [initial, open]);

  const toggleProject = (id) => {
    setSelectedProjectIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (initial) {
        await axios.put(`${API}/users/${initial.id}`, {
          username,
          email,
          role,
          project_ids: selectedProjectIds,
        });
        toast.success("User updated");
      } else {
        await axios.post(`${API}/users`, {
          username,
          email,
          password,
          role,
          project_ids: selectedProjectIds,
        });
        toast.success("User created");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error saving user");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-sm uppercase tracking-wider text-zinc-300">
            {initial ? "Edit User" : "New User"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">Username</Label>
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="bg-zinc-800 border-zinc-700 text-white rounded-none"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white rounded-none"
              />
            </div>
          </div>

          {!initial && (
            <div className="space-y-1">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-zinc-800 border-zinc-700 text-white rounded-none"
              />
            </div>
          )}

          <div className="space-y-1">
            <Label className="text-xs text-zinc-400 uppercase tracking-wider">Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white rounded-none">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700 text-white">
                {availableRoles.map((r) => (
                  <SelectItem key={r} value={r} className="hover:bg-zinc-800">
                    {ROLE_LABELS[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-zinc-400 uppercase tracking-wider">Projects</Label>
            <div className="flex flex-wrap gap-2">
              {projects.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => toggleProject(p.id)}
                  className={`text-xs px-2 py-1 border rounded-none transition-colors ${
                    selectedProjectIds.includes(p.id)
                      ? "bg-orange-600 border-orange-500 text-white"
                      : "bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-500"
                  }`}
                >
                  {p.name}
                </button>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none border-zinc-700 text-zinc-300">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="rounded-none bg-orange-600 hover:bg-orange-700">
              {loading ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PasswordDialog({ open, onClose, userId }) {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { setPw(""); }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/users/${userId}/password`, { password: pw });
      toast.success("Password updated");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error updating password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-sm uppercase tracking-wider text-zinc-300">Change Password</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <Label className="text-xs text-zinc-400 uppercase tracking-wider">New Password</Label>
            <Input
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              required
              minLength={6}
              className="bg-zinc-800 border-zinc-700 text-white rounded-none"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none border-zinc-700 text-zinc-300">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="rounded-none bg-orange-600 hover:bg-orange-700">
              {loading ? "Saving…" : "Update"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── MAIN ADMIN PAGE ─────────────────────────────────────────────────────────

export default function Admin() {
  const role = getRole();
  const currentUser = getUser();
  const isSuperAdmin = role === "super_admin";

  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [projectDialog, setProjectDialog] = useState({ open: false, initial: null });
  const [userDialog, setUserDialog] = useState({ open: false, initial: null });
  const [pwDialog, setPwDialog] = useState({ open: false, userId: null });
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { type: 'project'|'user', id, name }

  const fetchProjects = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/projects`);
      setProjects(res.data);
    } catch { toast.error("Failed to load projects"); }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data);
    } catch { toast.error("Failed to load users"); }
  }, []);

  useEffect(() => {
    fetchProjects();
    fetchUsers();
  }, [fetchProjects, fetchUsers]);

  const handleDeleteProject = async (id) => {
    try {
      await axios.delete(`${API}/projects/${id}`);
      toast.success("Project deleted");
      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Cannot delete project");
    }
    setDeleteConfirm(null);
  };

  const handleDeleteUser = async (id) => {
    try {
      await axios.delete(`${API}/users/${id}`);
      toast.success("User deleted");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Cannot delete user");
    }
    setDeleteConfirm(null);
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-xs font-semibold uppercase tracking-widest text-zinc-400 mb-6">
        Admin Console
      </h1>

      <Tabs defaultValue={isSuperAdmin ? "projects" : "users"}>
        <TabsList className="bg-zinc-900 border border-zinc-800 rounded-none h-auto p-0">
          {isSuperAdmin && (
            <TabsTrigger
              value="projects"
              className="rounded-none text-xs uppercase tracking-wider px-6 py-2.5 data-[state=active]:bg-zinc-800 data-[state=active]:text-orange-400"
            >
              Projects
            </TabsTrigger>
          )}
          <TabsTrigger
            value="users"
            className="rounded-none text-xs uppercase tracking-wider px-6 py-2.5 data-[state=active]:bg-zinc-800 data-[state=active]:text-orange-400"
          >
            Users
          </TabsTrigger>
        </TabsList>

        {/* ── PROJECTS TAB ── */}
        {isSuperAdmin && (
          <TabsContent value="projects" className="mt-4">
            <div className="flex justify-end mb-3">
              <Button
                size="sm"
                className="rounded-none bg-orange-600 hover:bg-orange-700 text-xs uppercase tracking-wider"
                onClick={() => setProjectDialog({ open: true, initial: null })}
              >
                <Plus size={14} className="mr-1" /> New Project
              </Button>
            </div>
            <div className="border border-zinc-800">
              <table className="w-full text-sm">
                <thead className="bg-zinc-900 text-zinc-400 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-2">Name</th>
                    <th className="text-left px-4 py-2">Description</th>
                    <th className="text-left px-4 py-2">Status</th>
                    <th className="px-4 py-2 w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((p) => (
                    <tr key={p.id} className="border-t border-zinc-800 text-zinc-200 hover:bg-zinc-800/40">
                      <td className="px-4 py-3 font-mono text-xs">{p.name}</td>
                      <td className="px-4 py-3 text-zinc-400 text-xs">{p.description}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] uppercase px-1.5 py-0.5 ${p.is_active ? "bg-green-900/50 text-green-400" : "bg-zinc-700 text-zinc-400"}`}>
                          {p.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-4 py-3 flex gap-1 justify-end">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-zinc-400 hover:text-white"
                          onClick={() => setProjectDialog({ open: true, initial: p })}
                        >
                          <Pencil size={13} />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-zinc-400 hover:text-red-400"
                          onClick={() => setDeleteConfirm({ type: "project", id: p.id, name: p.name })}
                        >
                          <Trash2 size={13} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {projects.length === 0 && (
                    <tr><td colSpan={4} className="text-center text-zinc-500 py-6 text-xs">No projects</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>
        )}

        {/* ── USERS TAB ── */}
        <TabsContent value="users" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button
              size="sm"
              className="rounded-none bg-orange-600 hover:bg-orange-700 text-xs uppercase tracking-wider"
              onClick={() => setUserDialog({ open: true, initial: null })}
            >
              <Plus size={14} className="mr-1" /> New User
            </Button>
          </div>
          <div className="border border-zinc-800">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900 text-zinc-400 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2">Username</th>
                  <th className="text-left px-4 py-2">Email</th>
                  <th className="text-left px-4 py-2">Role</th>
                  <th className="text-left px-4 py-2">Projects</th>
                  <th className="px-4 py-2 w-28"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const userProjects = projects.filter((p) => u.project_ids?.includes(p.id));
                  return (
                    <tr key={u.id} className="border-t border-zinc-800 text-zinc-200 hover:bg-zinc-800/40">
                      <td className="px-4 py-3 font-mono text-xs">
                        {u.username}
                        {u.id === currentUser?.id && (
                          <span className="ml-2 text-[10px] text-orange-400">(you)</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-zinc-400 text-xs">{u.email || "—"}</td>
                      <td className="px-4 py-3">
                        <Badge variant={ROLE_COLORS[u.role] ?? "outline"} className="text-[10px] uppercase">
                          {ROLE_LABELS[u.role] ?? u.role}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-zinc-400 text-xs">
                        {userProjects.length > 0
                          ? userProjects.map((p) => p.name).join(", ")
                          : "—"}
                      </td>
                      <td className="px-4 py-3 flex gap-1 justify-end">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-zinc-400 hover:text-white"
                          title="Change password"
                          onClick={() => setPwDialog({ open: true, userId: u.id })}
                        >
                          <KeyRound size={13} />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-zinc-400 hover:text-white"
                          onClick={() => setUserDialog({ open: true, initial: u })}
                        >
                          <Pencil size={13} />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-zinc-400 hover:text-red-400"
                          disabled={u.id === currentUser?.id}
                          onClick={() => setDeleteConfirm({ type: "user", id: u.id, name: u.username })}
                        >
                          <Trash2 size={13} />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {users.length === 0 && (
                  <tr><td colSpan={5} className="text-center text-zinc-500 py-6 text-xs">No users</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <ProjectDialog
        open={projectDialog.open}
        onClose={() => setProjectDialog({ open: false, initial: null })}
        onSaved={fetchProjects}
        initial={projectDialog.initial}
      />

      <UserDialog
        open={userDialog.open}
        onClose={() => setUserDialog({ open: false, initial: null })}
        onSaved={fetchUsers}
        initial={userDialog.initial}
        projects={projects}
      />

      <PasswordDialog
        open={pwDialog.open}
        onClose={() => setPwDialog({ open: false, userId: null })}
        userId={pwDialog.userId}
      />

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm uppercase tracking-wider text-red-400">
              Confirm Delete
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-300">
            Delete <span className="font-mono text-white">{deleteConfirm?.name}</span>? This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="rounded-none border-zinc-700 text-zinc-300">
              Cancel
            </Button>
            <Button
              className="rounded-none bg-red-700 hover:bg-red-800"
              onClick={() => {
                if (deleteConfirm?.type === "project") handleDeleteProject(deleteConfirm.id);
                else handleDeleteUser(deleteConfirm.id);
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
